import { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";

const BASE = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production-c773.up.railway.app");

const NashmanLogo = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="100" cy="100" r="100" fill="#0F172A"/>
    <path d="M100 52 C84 63 62 68 42 65 C53 79 70 84 86 80 C76 89 65 100 59 113 C72 108 84 99 91 88 C93 94 95 101 96 108 L100 117 L104 108 C105 101 107 94 109 88 C116 99 128 108 141 113 C135 100 124 89 114 80 C130 84 147 79 158 65 C138 68 116 63 100 52Z" fill="white"/>
    <ellipse cx="100" cy="49" rx="6" ry="7" fill="white"/>
    <path d="M87 120 C83 126 83 133 87 139 C83 145 83 151 87 157 C83 163 83 169 87 175" stroke="white" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
    <path d="M113 120 C117 126 117 133 113 139 C117 145 117 151 113 157 C117 163 117 169 113 175" stroke="white" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
    <line x1="87" y1="126" x2="113" y2="126" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="87" y1="136" x2="113" y2="136" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="87" y1="145" x2="113" y2="145" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="87" y1="154" x2="113" y2="154" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="87" y1="163" x2="113" y2="163" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="87" y1="172" x2="113" y2="172" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
);

const ROLES = [
  { id: "admin",        label: "Admin",        icon: "⚙", desc: "System administrator" },
  { id: "doctor",       label: "Doctor",       icon: "🩺", desc: "Physician / Clinician" },
  { id: "technician",   label: "Technician",   icon: "🔬", desc: "Lab technician" },
  { id: "receptionist", label: "Receptionist", icon: "🖥", desc: "Front desk" },
];

export default function Login() {
  const { login, loginMfa, backendOk, saveRememberDevice, getRememberedDevice } = useApp();

  const [step,     setStep]     = useState(1);
  const [selRole,  setSelRole]  = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [remember, setRemember] = useState(false);

  const [showForgot,    setShowForgot]    = useState(false);
  const [forgotMode,    setForgotMode]    = useState(null);
  const [forgotEmail,   setForgotEmail]   = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg,     setForgotMsg]     = useState("");
  const [forgotErr,     setForgotErr]     = useState("");
  const [forgotCountdown, setForgotCountdown] = useState(0);
  const [mfaResetCode,    setMfaResetCode]    = useState("");
  const [mfaResetNew,     setMfaResetNew]     = useState("");
  const [mfaResetCon,     setMfaResetCon]     = useState("");
  const [mfaResetLoading, setMfaResetLoading] = useState(false);
  const [mfaResetMsg,     setMfaResetMsg]     = useState("");
  const [mfaResetErr,     setMfaResetErr]     = useState("");

  // ── MFA login step (after correct password, admin with MFA enabled) ──────
  const [mfaStep,     setMfaStep]     = useState(false);
  const [mfaTempToken,setMfaTempToken]= useState(null);
  const [mfaCode,     setMfaCode]     = useState("");
  const [mfaLoading,  setMfaLoading]  = useState(false);
  const [mfaError,    setMfaError]    = useState("");

  useEffect(() => {
    const saved = getRememberedDevice();
    if (saved?.token) {
      import("../utils/api").then(({ authAPI, setToken }) => {
        authAPI.verifyToken(saved.token)
          .then(res => {
            if (res.valid) {
              setToken(saved.token);
              login(saved.username, null, saved.token);
            } else {
              localStorage.removeItem("emr_remember_device");
            }
          })
          .catch(() => localStorage.removeItem("emr_remember_device"));
      });
    }
  }, []);

  const selectRole = (role) => {
    setSelRole(role);
    const defaults = { admin: "admin", doctor: "doctor", technician: "technician", receptionist: "receptionist" };
    setUsername(defaults[role] || "");
    setPassword("");
    setError("");
    setStep(2);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    // Pass rememberMe flag so backend issues a 30-day token when checked
    const res = await login(username.trim(), password, null, remember);
    setLoading(false);

    if (res.mfaRequired) {
      // Password was correct — now show the 6-digit authenticator code screen
      setMfaTempToken(res.tempToken);
      setMfaStep(true);
      setMfaError("");
      setMfaCode("");
      return;
    }

    if (!res.success) {
      setError(res.error || "Invalid username or password.");
      return;
    }
    // Save the long-lived token returned by the backend directly
    if (remember && ["technician", "receptionist"].includes(selRole)) {
      saveRememberDevice(username.trim(), res.token);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setMfaError(""); setMfaLoading(true);
    const res = await loginMfa(mfaTempToken, mfaCode.trim(), remember);
    setMfaLoading(false);
    if (!res.success) {
      setMfaError(res.error || "Invalid authenticator code.");
      return;
    }
    if (remember) {
      saveRememberDevice(username.trim(), res.token);
    }
  };

  const cancelMfaStep = () => {
    setMfaStep(false); setMfaTempToken(null); setMfaCode(""); setMfaError("");
  };

  const handleForgotEmail = async () => {
    setForgotLoading(true); setForgotErr(""); setForgotMsg("");
    try {
      const res = await fetch(`${BASE}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await res.json();
      if (res.ok) {
        setForgotMsg(data.message || "Reset link sent to your email.");
        setForgotCountdown(data.expires_in_seconds || 180);
      } else setForgotErr(data.detail || "Failed to send.");
    } catch { setForgotErr("Network error. Please try again."); }
    finally { setForgotLoading(false); }
  };

  // Live countdown for the email reset link expiry
  useEffect(() => {
    if (forgotCountdown <= 0) return;
    const t = setTimeout(() => setForgotCountdown(c => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [forgotCountdown]);

  const handleMfaReset = async () => {
    setMfaResetErr(""); setMfaResetMsg("");
    if (!mfaResetCode) return setMfaResetErr("Enter your authenticator code or a backup code.");
    if (!mfaResetNew) return setMfaResetErr("New password cannot be empty.");
    if (mfaResetNew.length < 8) return setMfaResetErr("Password must be at least 8 characters.");
    if (mfaResetNew !== mfaResetCon) return setMfaResetErr("Passwords do not match.");
    setMfaResetLoading(true);
    try {
      const res = await fetch(`${BASE}/auth/mfa/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: mfaResetCode.trim(), new_password: mfaResetNew }),
      });
      const data = await res.json();
      if (res.ok) {
        setMfaResetMsg("Admin password reset successfully. You can now log in.");
        setMfaResetCode(""); setMfaResetNew(""); setMfaResetCon("");
      } else setMfaResetErr(data.detail || "Reset failed.");
    } catch { setMfaResetErr("Network error. Please try again."); }
    finally { setMfaResetLoading(false); }
  };

  const closeForgot = () => {
    setShowForgot(false); setForgotMode(null);
    setForgotEmail(""); setForgotMsg(""); setForgotErr(""); setForgotCountdown(0);
    setMfaResetCode(""); setMfaResetNew(""); setMfaResetCon(""); setMfaResetMsg(""); setMfaResetErr("");
  };

  return (
    <div className="login-page">
      <div style={{ width: 480 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginBottom: 12 }}>
            <NashmanLogo size={60} />
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: 21, fontWeight: 800, color: "#fff", letterSpacing: "-0.3px", lineHeight: 1.1 }}>NASHMAN INSTITUTE</div>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#94A3B8", letterSpacing: "1.8px", textTransform: "uppercase", marginTop: 4 }}>Pathology &amp; Regenerative Medicine</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "center", marginTop: 6 }}>
            <div style={{ height: 1, width: 60, background: "#334155" }} />
            <span style={{ fontSize: 11, color: "#94A3B8", letterSpacing: "0.5px" }}>EMR System</span>
            <div style={{ height: 1, width: 60, background: "#334155" }} />
          </div>
        </div>

        {backendOk === false && (
          <div className="alert alert-red" style={{ marginBottom: 16 }}>
            Cannot connect to backend. Make sure the server is running.
          </div>
        )}

        {step === 1 && (
          <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #E2E8F0", boxShadow: "0 4px 24px rgba(15,23,42,0.07)", padding: 32 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A", marginBottom: 6, textAlign: "center" }}>Select your role</div>
            <div style={{ fontSize: 12, color: "#94A3B8", textAlign: "center", marginBottom: 24 }}>Choose your role to continue</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {ROLES.map(r => (
                <button key={r.id} onClick={() => selectRole(r.id)} style={{
                  padding: "18px 14px", borderRadius: 12, border: "1.5px solid #E2E8F0",
                  background: "#F8FAFC", cursor: "pointer", textAlign: "center",
                  transition: "all 0.15s", display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#4F46E5"; e.currentTarget.style.background = "#EEF2FF"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#E2E8F0"; e.currentTarget.style.background = "#F8FAFC"; }}
                >
                  <span style={{ fontSize: 28 }}>{r.icon}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>{r.label}</span>
                  <span style={{ fontSize: 11, color: "#94A3B8" }}>{r.desc}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && selRole && mfaStep && (
          <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #E2E8F0", boxShadow: "0 4px 24px rgba(15,23,42,0.07)", padding: 32 }}>
            <button onClick={cancelMfaStep}
              style={{ background: "none", border: "none", color: "#64748B", fontSize: 13, cursor: "pointer", marginBottom: 16, display: "flex", alignItems: "center", gap: 5, padding: 0 }}>
              ← Back to login
            </button>

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
              <span style={{ fontSize: 28 }}>🔐</span>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>Two-factor verification</div>
                <div style={{ fontSize: 12, color: "#64748B" }}>Enter the code from your authenticator app</div>
              </div>
            </div>

            {mfaError && <div className="alert alert-red" style={{ marginBottom: 16 }}>{mfaError}</div>}

            <form onSubmit={handleMfaSubmit} autoComplete="off">
              <div className="form-group">
                <label className="form-label">6-digit code</label>
                <input className="input" value={mfaCode}
                  onChange={e => setMfaCode(e.target.value.replace(/[^0-9A-Za-z-]/g, "").slice(0, 9).toUpperCase())}
                  placeholder="000000" autoComplete="one-time-code" autoFocus required
                  style={{ fontSize: 22, letterSpacing: 4, textAlign: "center", fontFamily: "monospace" }} />
                <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>
                  Lost your phone? Enter one of your backup codes instead.
                </div>
              </div>
              <button className="btn btn-primary" type="submit"
                style={{ width: "100%", justifyContent: "center", padding: "11px", fontSize: 14 }}
                disabled={mfaLoading || mfaCode.length < 6}>
                {mfaLoading ? "Verifying..." : "Verify"}
              </button>
            </form>
          </div>
        )}

        {step === 2 && selRole && !mfaStep && (
          <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #E2E8F0", boxShadow: "0 4px 24px rgba(15,23,42,0.07)", padding: 32 }}>
            <button onClick={() => { setStep(1); setSelRole(null); setError(""); }}
              style={{ background: "none", border: "none", color: "#64748B", fontSize: 13, cursor: "pointer", marginBottom: 16, display: "flex", alignItems: "center", gap: 5, padding: 0 }}>
              ← Back to roles
            </button>

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
              <span style={{ fontSize: 28 }}>{ROLES.find(r => r.id === selRole)?.icon}</span>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>{ROLES.find(r => r.id === selRole)?.label} Login</div>
                <div style={{ fontSize: 12, color: "#64748B" }}>{ROLES.find(r => r.id === selRole)?.desc}</div>
              </div>
            </div>

            {error && <div className="alert alert-red" style={{ marginBottom: 16 }}>{error}</div>}

            <form onSubmit={handleLogin} autoComplete="off">
              <div className="form-group">
                <label className="form-label">Username</label>
                <input className="input" value={username} onChange={e => setUsername(e.target.value)}
                  placeholder="Enter username" autoComplete="off" autoFocus required />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <div style={{ position: "relative" }}>
                  <input className="input" type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="Enter password" autoComplete="new-password" required style={{ paddingRight: 38 }} />
                  <span onClick={() => setShowPassword(p => !p)}
                    style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#94A3B8", display: "flex", alignItems: "center" }}>
                    {showPassword
                      ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    }
                  </span>
                </div>
              </div>

              {["technician", "receptionist"].includes(selRole) && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <input type="checkbox" id="remember" checked={remember} onChange={e => setRemember(e.target.checked)}
                    style={{ width: 15, height: 15, accentColor: "#4F46E5", cursor: "pointer" }} />
                  <label htmlFor="remember" style={{ fontSize: 12, color: "#64748B", cursor: "pointer" }}>
                    Remember this device for 90 days
                  </label>
                </div>
              )}

              <button className="btn btn-primary" type="submit"
                style={{ width: "100%", justifyContent: "center", padding: "11px", fontSize: 14 }}
                disabled={loading || backendOk === false}>
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            {selRole === "admin" && (
              <div style={{ textAlign: "center", marginTop: 14 }}>
                <button onClick={() => setShowForgot(true)}
                  style={{ background: "none", border: "none", color: "#4F46E5", fontSize: 12, cursor: "pointer" }}>
                  Forgot admin password?
                </button>
              </div>
            )}
            {selRole !== "admin" && error?.toLowerCase().includes("maintenance") && (
              <div style={{ textAlign: "center", marginTop: 10 }}>
                <span style={{ fontSize: 11, color: "#64748B" }}>Admin? </span>
                <button onClick={() => { setStep(1); setSelRole(null); setError(""); }}
                  style={{ background: "none", border: "none", color: "#4F46E5", fontSize: 12, cursor: "pointer" }}>
                  Switch to Admin login
                </button>
              </div>
            )}
          </div>
        )}

        <div style={{ textAlign: "center", fontSize: 11, color: "#94A3B8", marginTop: 16 }}>
          Nashman Institute EMR v2.0 · Secure Clinical Records
        </div>
      </div>

      {showForgot && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <div style={{ fontWeight: 700, fontSize: 16 }}>Reset Admin Password</div>
              <button className="btn btn-ghost btn-sm" onClick={closeForgot}>✕</button>
            </div>
            <div className="modal-body">
              {!forgotMode && (
                <div>
                  <p style={{ fontSize: 13, color: "#64748B", marginBottom: 18 }}>How would you like to reset the admin password?</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <button onClick={() => setForgotMode("email")} style={{ padding: "14px 16px", borderRadius: 10, border: "1.5px solid #E2E8F0", background: "#F8FAFC", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 12 }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = "#4F46E5"; e.currentTarget.style.background = "#EEF2FF"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = "#E2E8F0"; e.currentTarget.style.background = "#F8FAFC"; }}
                    >
                      <span style={{ fontSize: 24 }}>📧</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>Reset via Email</div>
                        <div style={{ fontSize: 11, color: "#64748B" }}>A reset link will be sent to your recovery email</div>
                      </div>
                    </button>
                    <button onClick={() => setForgotMode("mfa")} style={{ padding: "14px 16px", borderRadius: 10, border: "1.5px solid #E2E8F0", background: "#F8FAFC", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 12 }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = "#4F46E5"; e.currentTarget.style.background = "#EEF2FF"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = "#E2E8F0"; e.currentTarget.style.background = "#F8FAFC"; }}
                    >
                      <span style={{ fontSize: 24 }}>🔐</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>Reset via Authenticator</div>
                        <div style={{ fontSize: 11, color: "#64748B" }}>Enter your 6-digit authenticator code to reset directly</div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
              {forgotMode === "email" && (
                <div>
                  <button onClick={() => setForgotMode(null)} style={{ background: "none", border: "none", color: "#64748B", fontSize: 12, cursor: "pointer", marginBottom: 14, padding: 0 }}>← Back</button>
                  {forgotErr && <div className="alert alert-red">{forgotErr}</div>}
                  {forgotMsg ? (
                    <div>
                      <div className="alert alert-green">{forgotMsg}</div>
                      {forgotCountdown > 0 ? (
                        <p style={{ fontSize: 13, color: "#0F172A", marginTop: 10, fontWeight: 600, textAlign: "center" }}>
                          Link expires in {Math.floor(forgotCountdown / 60)}:{String(forgotCountdown % 60).padStart(2, "0")}
                        </p>
                      ) : (
                        <p style={{ fontSize: 12, color: "#DC2626", marginTop: 10, fontWeight: 600, textAlign: "center" }}>
                          Link has expired. Please request a new one.
                        </p>
                      )}
                      <p style={{ fontSize: 12, color: "#64748B", marginTop: 4, textAlign: "center" }}>Check your inbox for the reset link.</p>
                    </div>
                  ) : (
                    <div>
                      <div className="alert alert-blue" style={{ marginBottom: 14 }}>Enter the recovery email saved in Settings → Passwords.</div>
                      <div className="form-group">
                        <label className="form-label">Recovery Email</label>
                        <input className="input" type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="admin@example.com" autoFocus />
                      </div>
                    </div>
                  )}
                </div>
              )}
              {forgotMode === "mfa" && (
                <div>
                  <button onClick={() => setForgotMode(null)} style={{ background: "none", border: "none", color: "#64748B", fontSize: 12, cursor: "pointer", marginBottom: 14, padding: 0 }}>← Back</button>
                  {mfaResetErr && <div className="alert alert-red">{mfaResetErr}</div>}
                  {mfaResetMsg ? (
                    <div className="alert alert-green">{mfaResetMsg}</div>
                  ) : (
                    <div>
                      <div className="form-group">
                        <label className="form-label">Authenticator code or backup code</label>
                        <input className="input" value={mfaResetCode}
                          onChange={e => setMfaResetCode(e.target.value.replace(/[^0-9A-Za-z-]/g, "").slice(0, 9).toUpperCase())}
                          placeholder="000000" autoFocus
                          style={{ fontSize: 18, letterSpacing: 4, textAlign: "center", fontFamily: "monospace" }} />
                        <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>
                          Lost your phone? Enter one of your backup codes instead.
                        </div>
                      </div>
                      <div className="form-grid-2">
                        <div className="form-group">
                          <label className="form-label">New Admin Password</label>
                          <input className="input" type="password" value={mfaResetNew} onChange={e => setMfaResetNew(e.target.value)} placeholder="Min 8 characters" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Confirm</label>
                          <input className="input" type="password" value={mfaResetCon} onChange={e => setMfaResetCon(e.target.value)} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeForgot}>Cancel</button>
              {forgotMode === "email" && !forgotMsg && (
                <button className="btn btn-primary" disabled={forgotLoading || !forgotEmail} onClick={handleForgotEmail}>
                  {forgotLoading ? "Sending..." : "Send Reset Link"}
                </button>
              )}
              {forgotMode === "mfa" && !mfaResetMsg && (
                <button className="btn btn-primary" disabled={mfaResetLoading || mfaResetCode.length < 6 || !mfaResetNew} onClick={handleMfaReset}>
                  {mfaResetLoading ? "Resetting..." : "Reset Password"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}